import { DailyDiscoveryPayloadSchema, type DailyDiscoveryPayload } from '@entangled-vega/shared';

const ASSET_META_NAME = 'entangled-vega-daily-discovery';
const SNAPSHOT_META_NAME = 'entangled-vega-snapshot-date';
const ASSET_PATTERN = /^\/assets\/daily-discovery\.([a-f0-9]{16})\.json$/u;

export class DailyDiscoveryLoadError extends Error {
  override name = 'DailyDiscoveryLoadError';
}

export function dailyDiscoveryDocumentMetadata(documentValue: Document = document): {
  assetPath: string | null;
  snapshotDate: string | null;
} {
  const assetPath =
    documentValue
      .querySelector<HTMLMetaElement>(`meta[name="${ASSET_META_NAME}"]`)
      ?.content.trim() ?? null;
  const snapshotDate =
    documentValue
      .querySelector<HTMLMetaElement>(`meta[name="${SNAPSHOT_META_NAME}"]`)
      ?.content.trim() ?? null;
  return {
    assetPath: assetPath !== null && ASSET_PATTERN.test(assetPath) ? assetPath : null,
    snapshotDate:
      snapshotDate !== null && /^\d{4}-\d{2}-\d{2}$/u.test(snapshotDate) ? snapshotDate : null,
  };
}

async function digestPrefix(value: string): Promise<string> {
  const bytes = new TextEncoder().encode(value);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')
    .slice(0, 16);
}

export async function loadDailyDiscovery(options: {
  assetPath: string;
  fetcher?: typeof fetch;
  signal?: AbortSignal;
}): Promise<DailyDiscoveryPayload> {
  const match = ASSET_PATTERN.exec(options.assetPath);
  if (match === null) {
    throw new DailyDiscoveryLoadError('The daily discovery asset path is invalid.');
  }
  const fetcher = options.fetcher ?? globalThis.fetch;
  const response = await fetcher(options.assetPath, {
    cache: 'force-cache',
    ...(options.signal === undefined ? {} : { signal: options.signal }),
  });
  if (!response.ok) {
    throw new DailyDiscoveryLoadError(
      `The daily discovery asset returned HTTP ${response.status}.`,
    );
  }
  const text = await response.text();
  if ((await digestPrefix(text)) !== match[1]) {
    throw new DailyDiscoveryLoadError('The daily discovery asset failed its integrity check.');
  }
  let value: unknown;
  try {
    value = JSON.parse(text) as unknown;
  } catch {
    throw new DailyDiscoveryLoadError('The daily discovery payload is not valid JSON.');
  }
  const parsed = DailyDiscoveryPayloadSchema.safeParse(value);
  if (!parsed.success) {
    throw new DailyDiscoveryLoadError('The daily discovery payload is invalid.');
  }
  return parsed.data;
}
